"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createProposalAction,
  updateProposalAction,
  updateProposalStatusAction,
  deleteProposalAction,
  createContractAction,
  deleteContractAction,
} from "@/actions/contracts-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  FileText,
  Search,
  Mail,
  Euro,
  Trash2,
  ExternalLink,
  Plus,
  ArrowRightLeft,
  ChevronRight,
  Calculator,
  Calendar,
  Layers,
  Link as LinkIcon,
  X,
  FileCheck,
  Pencil,
  TrendingUp,
} from "lucide-react";

interface LineItem {
  description: string;
  qty: number;
  price: number;
}

interface ProposalItem {
  id: string;
  title: string;
  clientName: string;
  contactEmail: string;
  value: string;
  status: "draft" | "sent" | "accepted";
  lineItems: LineItem[];
  leadId: string;
  fileUrl?: string | null;
  createdAt: string;
}

interface ContractItem {
  id: string;
  title: string;
  fileUrl: string | null;
  proposalId: string;
  notes: string;
  createdAt: string;
}

interface ProposalsClientProps {
  initialProposals: ProposalItem[];
  initialContracts: ContractItem[];
  leads: Array<{ id: string; name: string; contactEmail: string }>;
}

const DEFAULT_MOCK_PROPOSALS: ProposalItem[] = [
  {
    id: "prop_1",
    title: "Sviluppo Portale Web & CMS",
    clientName: "Acme SpA",
    contactEmail: "sales@acme.com",
    value: "15000",
    status: "accepted",
    lineItems: [
      { description: "Progettazione UX/UI & Prototipo", qty: 1, price: 4000 },
      {
        description: "Sviluppo Frontend & Integrazione CMS",
        qty: 1,
        price: 8000,
      },
      { description: "Rilascio, Collaudo & Training", qty: 1, price: 3000 },
    ],
    leadId: "lead_1",
    fileUrl: "https://pdf-server.com/contracts/acme-cms-signed.pdf",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "prop_2",
    title: "Infrastruttura AWS & Migrazione Cloud",
    clientName: "Globex srl",
    contactEmail: "p.scorpio@globex.co",
    value: "35000",
    status: "sent",
    lineItems: [
      {
        description: "Audit Architetturale & Cloud Assessment",
        qty: 1,
        price: 5000,
      },
      {
        description: "Configurazione AWS landing zones & IaC",
        qty: 1,
        price: 18000,
      },
      {
        description: "Migrazione Database & Data Validation",
        qty: 1,
        price: 12000,
      },
    ],
    leadId: "lead_2",
    createdAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "prop_3",
    title: "Audit di Sicurezza & Vulnerability Assessment",
    clientName: "Initech Corp",
    contactEmail: "gibbons@initech.com",
    value: "6500",
    status: "draft",
    lineItems: [
      {
        description: "Vulnerability scanning & Manual pentesting",
        qty: 1,
        price: 6500,
      },
    ],
    leadId: "lead_3",
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const DEFAULT_MOCK_CONTRACTS: ContractItem[] = [
  {
    id: "contr_1",
    title: "Contratto Esecutivo Sviluppo CMS Acme SpA",
    fileUrl: "https://pdf-server.com/contracts/acme-cms-signed.pdf",
    proposalId: "prop_1",
    notes: "Contratto controfirmato digitalmente. Inizio lavori 15/07/2026.",
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

export function ProposalsClient({
  initialProposals,
  initialContracts,
  leads,
}: ProposalsClientProps) {
  const router = useRouter();

  // State initialized with DB data if present, otherwise mock
  const [proposals, setProposals] = useState<ProposalItem[]>(() => {
    return initialProposals.length > 0
      ? initialProposals
      : DEFAULT_MOCK_PROPOSALS;
  });

  const [contracts, setContracts] = useState<ContractItem[]>(() => {
    return initialContracts.length > 0
      ? initialContracts
      : DEFAULT_MOCK_CONTRACTS;
  });

  const [search, setSearch] = useState("");
  const [isProposalModalOpen, setIsProposalModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Edit / Form states for Proposal
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(
    null,
  );
  const [proposalTitle, setProposalTitle] = useState("");
  const [clientName, setClientName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [proposalStatus, setProposalStatus] = useState<
    "draft" | "sent" | "accepted"
  >("draft");
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: "", qty: 1, price: 0 },
  ]);
  const [leadId, setLeadId] = useState("");

  // Form states for Contract Linker
  const [selectedProposalForContract, setSelectedProposalForContract] =
    useState<ProposalItem | null>(null);
  const [contractTitle, setContractTitle] = useState("");
  const [contractFileUrl, setContractFileUrl] = useState("");
  const [contractNotes, setContractNotes] = useState("");

  useEffect(() => {
    if (initialProposals.length > 0) {
      setProposals(initialProposals);
    }
  }, [initialProposals]);

  useEffect(() => {
    if (initialContracts.length > 0) {
      setContracts(initialContracts);
    }
  }, [initialContracts]);

  // Open modal for new proposal
  const handleNewProposal = () => {
    setSelectedProposalId(null);
    setProposalTitle("");
    setClientName("");
    setContactEmail("");
    setProposalStatus("draft");
    setLineItems([{ description: "", qty: 1, price: 0 }]);
    setLeadId("");
    setError("");
    setIsProposalModalOpen(true);
  };

  // Open modal for editing proposal
  const handleEditProposal = (prop: ProposalItem) => {
    // If proposal is accepted, freeze edits
    if (prop.status === "accepted") {
      alert(
        "Le proposte accettate sono congelate. Non è possibile modificarle direttamente.",
      );
      return;
    }
    setSelectedProposalId(prop.id);
    setProposalTitle(prop.title);
    setClientName(prop.clientName);
    setContactEmail(prop.contactEmail);
    setProposalStatus(prop.status);
    setLineItems(
      prop.lineItems.length > 0
        ? prop.lineItems
        : [{ description: "", qty: 1, price: 0 }],
    );
    setLeadId(prop.leadId || "");
    setError("");
    setIsProposalModalOpen(true);
  };

  // Open contract linker modal
  const handleOpenContractLinker = (prop: ProposalItem) => {
    setSelectedProposalForContract(prop);
    setContractTitle(`Contratto per ${prop.title}`);

    // Check if contract already exists
    const existingContract = contracts.find((c) => c.proposalId === prop.id);
    if (existingContract) {
      setContractTitle(existingContract.title);
      setContractFileUrl(existingContract.fileUrl || "");
      setContractNotes(existingContract.notes || "");
    } else {
      setContractFileUrl("");
      setContractNotes("");
    }

    setError("");
    setIsContractModalOpen(true);
  };

  // Handle lead selection sync
  const handleLeadChange = (id: string) => {
    setLeadId(id);
    const selectedLead = leads.find((l) => l.id === id);
    if (selectedLead) {
      setClientName(selectedLead.name);
      setContactEmail(selectedLead.contactEmail);
    }
  };

  // Line items helper
  const addLineItem = () => {
    setLineItems([...lineItems, { description: "", qty: 1, price: 0 }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index: number, field: keyof LineItem, val: any) => {
    const updated = lineItems.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          [field]: field === "description" ? val : Number(val),
        };
      }
      return item;
    });
    setLineItems(updated);
  };

  // Total calculation
  const calculateTotal = (items: LineItem[]) => {
    return items.reduce((acc, curr) => acc + curr.qty * curr.price, 0);
  };

  // Handle Proposal Submission
  const handleProposalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!proposalTitle.trim()) {
      setError("Il titolo dell'offerta è obbligatorio.");
      return;
    }
    if (!clientName.trim()) {
      setError("Il nome del cliente è obbligatorio.");
      return;
    }
    if (
      lineItems.some(
        (item) => !item.description.trim() || item.price < 0 || item.qty <= 0,
      )
    ) {
      setError(
        "Assicurati che tutte le righe dell'offerta abbiano una descrizione e valori numerici validi.",
      );
      return;
    }

    setLoading(true);
    const calculatedValue = calculateTotal(lineItems).toString();

    try {
      if (selectedProposalId) {
        // Edit existing
        const res = await updateProposalAction(selectedProposalId, {
          title: proposalTitle,
          clientName,
          contactEmail,
          value: calculatedValue,
          status: proposalStatus,
          lineItems,
          leadId,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsProposalModalOpen(false);
          router.refresh();
          if (initialProposals.length === 0) {
            setProposals((prev) =>
              prev.map((p) =>
                p.id === selectedProposalId
                  ? {
                      ...p,
                      title: proposalTitle,
                      clientName,
                      contactEmail,
                      value: calculatedValue,
                      status: proposalStatus,
                      lineItems,
                      leadId,
                    }
                  : p,
              ),
            );
          }
        }
      } else {
        // Create new
        const res = await createProposalAction({
          title: proposalTitle,
          clientName,
          contactEmail,
          value: calculatedValue,
          status: proposalStatus,
          lineItems,
          leadId,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsProposalModalOpen(false);
          router.refresh();
          if (initialProposals.length === 0) {
            const newProp: ProposalItem = {
              id: res.id || `proposal_${Date.now()}`,
              title: proposalTitle,
              clientName,
              contactEmail,
              value: calculatedValue,
              status: proposalStatus,
              lineItems,
              leadId,
              createdAt: new Date().toISOString(),
            };
            setProposals((prev) => [newProp, ...prev]);
          }
        }
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio.");
    } finally {
      setLoading(false);
    }
  };

  // Update Status Action
  const handleStatusChange = async (id: string, newStatus: any) => {
    // If proposal is accepted, block changes unless user changes it back with permission
    const current = proposals.find((p) => p.id === id);
    if (current && current.status === "accepted" && newStatus !== "accepted") {
      if (
        !confirm(
          "Questa proposta è stata accettata ed è congelata. Vuoi davvero sbloccarla riportandola allo stato precedente?",
        )
      ) {
        return;
      }
    }

    try {
      const res = await updateProposalStatusAction(id, newStatus);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
        setProposals((prev) =>
          prev.map((p) => (p.id === id ? { ...p, status: newStatus } : p)),
        );
      }
    } catch (err) {
      alert("Impossibile aggiornare lo stato.");
    }
  };

  // Delete Proposal
  const handleDeleteProposal = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questa proposta?")) return;

    try {
      const res = await deleteProposalAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
        setProposals((prev) => prev.filter((p) => p.id !== id));
        setContracts((prev) => prev.filter((c) => c.proposalId !== id));
      }
    } catch (err) {
      alert("Errore durante l'eliminazione.");
    }
  };

  // Submit Link Contract
  const handleLinkContract = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProposalForContract) return;
    setError("");

    if (!contractTitle.trim()) {
      setError("Il titolo del contratto è obbligatorio.");
      return;
    }
    if (!contractFileUrl.trim() || !contractFileUrl.startsWith("http")) {
      setError(
        "Inserire un URL valido per il file del contratto (es. https://...)",
      );
      return;
    }

    setLoading(true);

    try {
      const res = await createContractAction({
        title: contractTitle,
        fileUrl: contractFileUrl,
        proposalId: selectedProposalForContract.id,
        notes: contractNotes,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsContractModalOpen(false);
        router.refresh();

        // Update the contract locally
        if (initialContracts.length === 0) {
          const newContract: ContractItem = {
            id: res.id || `contract_${Date.now()}`,
            title: contractTitle,
            fileUrl: contractFileUrl,
            proposalId: selectedProposalForContract.id,
            notes: contractNotes,
            createdAt: new Date().toISOString(),
          };
          setContracts((prev) => {
            const filtered = prev.filter(
              (c) => c.proposalId !== selectedProposalForContract.id,
            );
            return [...filtered, newContract];
          });
        }

        // Also update proposal local fileUrl link
        setProposals((prev) =>
          prev.map((p) =>
            p.id === selectedProposalForContract.id
              ? { ...p, fileUrl: contractFileUrl }
              : p,
          ),
        );
      }
    } catch (err: any) {
      setError(err.message || "Impossibile collegare il contratto.");
    } finally {
      setLoading(false);
    }
  };

  // Delete Contract Link
  const handleDeleteContract = async (proposalId: string) => {
    const contr = contracts.find((c) => c.proposalId === proposalId);
    if (!contr) return;

    if (
      !confirm(
        "Sei sicuro di voler rimuovere il vincolo contrattuale da questa proposta?",
      )
    )
      return;

    try {
      const res = await deleteContractAction(contr.id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
        setContracts((prev) => prev.filter((c) => c.id !== contr.id));
        setProposals((prev) =>
          prev.map((p) => (p.id === proposalId ? { ...p, fileUrl: null } : p)),
        );
        setIsContractModalOpen(false);
      }
    } catch (err) {
      alert("Errore durante la rimozione del contratto.");
    }
  };

  const formatCurrency = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return "€0";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const filteredProposals = proposals.filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.clientName.toLowerCase().includes(search.toLowerCase()) ||
      p.contactEmail.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      {/* Search and Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca offerte commerciali..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button
          onClick={handleNewProposal}
          className="gap-1.5 shrink-0 w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          Nuova Offerta
        </Button>
      </div>

      {/* Proposals list grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {filteredProposals.length === 0 ? (
          <div className="xl:col-span-2 flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center space-y-3">
            <FileText className="h-10 w-10 text-text-muted animate-pulse" />
            <h3 className="font-bold text-text-primary">
              Nessuna Proposta Commerciale
            </h3>
            <p className="text-sm text-text-secondary max-w-sm">
              Crea la tua prima offerta commerciale per inviarla al cliente o
              collegare un accordo formale.
            </p>
            <Button onClick={handleNewProposal} size="sm" className="mt-2">
              Inizia Ora
            </Button>
          </div>
        ) : (
          filteredProposals.map((prop) => {
            const totalVal = calculateTotal(prop.lineItems || []);
            const hasContract = !!contracts.find(
              (c) => c.proposalId === prop.id,
            );
            const contract = contracts.find((c) => c.proposalId === prop.id);

            return (
              <Card
                key={prop.id}
                onClick={() => handleEditProposal(prop)}
                className="relative hover:shadow-md hover:border-primary-500/30 transition-all border border-border group cursor-pointer select-none"
              >
                {/* Hover Action Bar */}
                <div
                  className="absolute top-2.5 right-2.5 z-10 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center gap-1 bg-background/90 backdrop-blur-sm p-1 rounded-lg border border-border shadow-sm"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  <Link
                    href={`/explore?nodeId=${prop.id}`}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    title="Vedi nel Grafo"
                    className="p-1 hover:bg-primary-500/10 text-text-secondary hover:text-primary-600 rounded-md transition-colors"
                  >
                    <TrendingUp className="h-3.5 w-3.5" />
                  </Link>
                  {prop.status !== "accepted" && (
                    <button
                      type="button"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleEditProposal(prop);
                      }}
                      title="Modifica"
                      className="p-1 hover:bg-primary-500/10 text-text-secondary hover:text-primary-600 rounded-md transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleDeleteProposal(prop.id);
                    }}
                    title="Elimina"
                    className="p-1 hover:bg-error-soft text-text-secondary hover:text-error rounded-md transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                <CardHeader className="pb-3 border-b border-border/40">
                  <div className="flex justify-between items-start gap-4">
                    <div className="space-y-1">
                      <div
                        className="flex items-center gap-2"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <Badge
                          variant={
                            prop.status === "accepted"
                              ? "success"
                              : prop.status === "sent"
                                ? "warning"
                                : "neutral"
                          }
                        >
                          {prop.status === "accepted"
                            ? "Accettata"
                            : prop.status === "sent"
                              ? "Inviata"
                              : "Bozza"}
                        </Badge>
                        {hasContract && (
                          <Badge variant="info" className="gap-1">
                            <FileCheck className="h-3 w-3" />
                            Contratto Collegato
                          </Badge>
                        )}
                      </div>
                      <Link
                        href={`/explore?nodeId=${prop.id}`}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        className="block"
                      >
                        <h3 className="font-bold text-text-primary text-base group-hover:text-primary-600 transition-colors">
                          {prop.title}
                        </h3>
                      </Link>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="py-4 space-y-4">
                  {/* Client and Value details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="text-[10px] uppercase font-bold text-text-muted tracking-wider">
                        Cliente
                      </span>
                      <div className="font-semibold text-sm text-text-primary truncate">
                        {prop.clientName}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-text-secondary">
                        <Mail className="h-3 w-3 shrink-0" />
                        <span className="truncate">
                          {prop.contactEmail || "Nessuna email"}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <span className="text-[10px] uppercase font-bold text-text-muted tracking-wider">
                        Valore Offerta
                      </span>
                      <div className="font-bold text-lg text-text-primary flex items-center gap-0.5">
                        <Euro className="h-4 w-4 text-success" />
                        <span>
                          {formatCurrency(totalVal.toString() || prop.value)}
                        </span>
                      </div>
                      <div className="text-xs text-text-muted">IVA esclusa</div>
                    </div>
                  </div>

                  {/* Line Items Preview */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-[10px] uppercase font-bold text-text-muted tracking-wider">
                      <span>
                        Voci di Dettaglio ({prop.lineItems?.length || 0})
                      </span>
                      <Calculator className="h-3.5 w-3.5 text-text-muted" />
                    </div>
                    <div className="max-h-24 overflow-y-auto space-y-1.5 border border-border/30 rounded-lg p-2 bg-card-muted/30 scrollbar-thin">
                      {prop.lineItems?.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between text-xs text-text-secondary"
                        >
                          <span className="truncate max-w-[250px]">
                            {item.description}
                          </span>
                          <span className="font-semibold shrink-0">
                            {item.qty}x {formatCurrency(item.price.toString())}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="pt-3 border-t border-border/40 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-text-muted">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      Creata il{" "}
                      {new Date(prop.createdAt).toLocaleDateString("it-IT")}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Status updater */}
                    <div className="flex items-center gap-1">
                      <ArrowRightLeft className="h-3.5 w-3.5 text-text-secondary" />
                      <select
                        value={prop.status}
                        onChange={(e) =>
                          handleStatusChange(prop.id, e.target.value)
                        }
                        className="text-xs font-semibold text-text-secondary bg-transparent border-0 outline-none cursor-pointer focus:ring-0 hover:text-primary-600"
                      >
                        <option value="draft">Bozza</option>
                        <option value="sent">Inviata</option>
                        <option value="accepted">Accettata (Congelata)</option>
                      </select>
                    </div>

                    {/* Contract actions */}
                    {hasContract && contract ? (
                      <div className="flex items-center gap-1.5">
                        <a
                          href={contract.fileUrl || undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:underline cursor-pointer"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Vedi Contratto
                        </a>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenContractLinker(prop)}
                          title="Gestisci Contratto"
                          className="h-7 px-2 text-xs"
                        >
                          Modifica
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleOpenContractLinker(prop)}
                        className="gap-1 h-7 text-xs px-2"
                      >
                        <LinkIcon className="h-3 w-3" />
                        Collega Accordo
                      </Button>
                    )}
                  </div>
                </CardFooter>
              </Card>
            );
          })
        )}
      </div>

      {/* Proposal Add / Edit Dialog */}
      <Dialog open={isProposalModalOpen} onOpenChange={setIsProposalModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedProposalId
                ? "Modifica Offerta Commerciale"
                : "Nuova Offerta Commerciale"}
            </DialogTitle>
            <DialogDescription>
              Compila le informazioni del cliente e definisci le righe di
              preventivo con quantità e prezzi.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleProposalSubmit} className="space-y-4 my-2">
            {/* Associated Lead Selection */}
            <div className="space-y-1.5">
              <Label htmlFor="assoc-lead">
                Associa Opportunity / Lead (Opzionale)
              </Label>
              <Select
                id="assoc-lead"
                value={leadId}
                onChange={(e) => handleLeadChange(e.target.value)}
                disabled={loading}
              >
                <option value="">
                  -- Seleziona un Lead Commerciale attivo --
                </option>
                {leads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.contactEmail})
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="prop-title">Oggetto dell'offerta</Label>
                <Input
                  id="prop-title"
                  placeholder="es. Sviluppo Applicativo CRM v2"
                  value={proposalTitle}
                  onChange={(e) => setProposalTitle(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="prop-status">Stato Iniziale</Label>
                <Select
                  id="prop-status"
                  value={proposalStatus}
                  onChange={(e) => setProposalStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="draft">Bozza (Draft)</option>
                  <option value="sent">Inviato al Cliente (Sent)</option>
                  <option value="accepted">Accettato (Accepted)</option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="client-name">Nome Cliente</Label>
                <Input
                  id="client-name"
                  placeholder="es. Acme Corp Inc."
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="client-email">Email di Contatto</Label>
                <Input
                  id="client-email"
                  type="email"
                  placeholder="es. billing@acme.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Line Items Section */}
            <div className="border-t border-border/50 pt-4 space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-xs uppercase font-bold tracking-wider text-text-muted">
                  Righe Preventivo (Quantità x Prezzo)
                </Label>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={addLineItem}
                  className="h-7 px-2 text-xs gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Aggiungi Riga
                </Button>
              </div>

              <div className="space-y-2.5 max-h-56 overflow-y-auto pr-1 scrollbar-thin">
                {lineItems.map((item, idx) => (
                  <div key={idx} className="flex gap-3 items-end">
                    <div className="flex-1 space-y-1">
                      {idx === 0 && (
                        <Label className="text-[10px] text-text-muted">
                          Descrizione del servizio/prodotto
                        </Label>
                      )}
                      <Input
                        placeholder="es. Setup Server AWS"
                        value={item.description}
                        onChange={(e) =>
                          updateLineItem(idx, "description", e.target.value)
                        }
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="w-20 space-y-1">
                      {idx === 0 && (
                        <Label className="text-[10px] text-text-muted">
                          Quantità
                        </Label>
                      )}
                      <Input
                        type="number"
                        min="1"
                        value={item.qty}
                        onChange={(e) =>
                          updateLineItem(idx, "qty", e.target.value)
                        }
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="w-32 space-y-1">
                      {idx === 0 && (
                        <Label className="text-[10px] text-text-muted">
                          Prezzo (€)
                        </Label>
                      )}
                      <Input
                        type="number"
                        min="0"
                        placeholder="Prezzo"
                        value={item.price || ""}
                        onChange={(e) =>
                          updateLineItem(idx, "price", e.target.value)
                        }
                        disabled={loading}
                        required
                      />
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLineItem(idx)}
                      disabled={lineItems.length === 1 || loading}
                      className="h-9 w-9 p-0 hover:bg-error-soft text-text-muted hover:text-error shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Summary display */}
              <div className="flex justify-between items-center p-3 rounded-lg bg-card-muted/40 border border-border/40 font-bold text-sm text-text-primary">
                <span>TOTALE GENERATO</span>
                <span className="text-base text-success">
                  {formatCurrency(calculateTotal(lineItems).toString())}
                </span>
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2 border-t border-border/50">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsProposalModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {selectedProposalId ? "Salva Modifiche" : "Crea Offerta"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contract Linker Dialog */}
      <Dialog open={isContractModalOpen} onOpenChange={setIsContractModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Collega Accordo Contrattuale</DialogTitle>
            <DialogDescription>
              Collega un accordo formale finalizzato (PDF caricato o indirizzo
              web) a questa proposta.
            </DialogDescription>
          </DialogHeader>

          {selectedProposalForContract && (
            <form onSubmit={handleLinkContract} className="space-y-4 my-2">
              <div className="p-3 bg-primary-500/5 rounded-lg border border-primary-500/20 text-xs space-y-1">
                <div className="font-semibold text-text-primary">
                  Offerta Associata: {selectedProposalForContract.title}
                </div>
                <div className="text-text-secondary">
                  Cliente: {selectedProposalForContract.clientName}
                </div>
                <div className="text-text-secondary font-bold">
                  Valore:{" "}
                  {formatCurrency(
                    calculateTotal(
                      selectedProposalForContract.lineItems || [],
                    ).toString(),
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contract-title">
                  Titolo del Contratto / Riferimento
                </Label>
                <Input
                  id="contract-title"
                  placeholder="es. Accordo di Servizio Acme SpA signed.pdf"
                  value={contractTitle}
                  onChange={(e) => setContractTitle(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contract-url">
                  URL Documento PDF Contratto
                </Label>
                <Input
                  id="contract-url"
                  placeholder="es. https://cloud-storage.com/signed/acme-2026.pdf"
                  value={contractFileUrl}
                  onChange={(e) => setContractFileUrl(e.target.value)}
                  disabled={loading}
                  required
                />
                <p className="text-[10px] text-text-muted">
                  Inserire il link del file firmato archiviato in storage.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contract-notes">Note Esecutive & Termini</Label>
                <textarea
                  id="contract-notes"
                  className="flex min-h-[80px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
                  placeholder="Termini di pagamento, date di rinnovo, clausole specifiche..."
                  value={contractNotes}
                  onChange={(e) => setContractNotes(e.target.value)}
                  disabled={loading}
                />
              </div>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <div className="flex justify-between items-center pt-2 border-t border-border/50">
                {contracts.some(
                  (c) => c.proposalId === selectedProposalForContract.id,
                ) ? (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      handleDeleteContract(selectedProposalForContract.id)
                    }
                    disabled={loading}
                    className="hover:bg-error-soft text-error gap-1 px-2.5 text-xs h-9"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Rimuovi Contratto
                  </Button>
                ) : (
                  <div />
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setIsContractModalOpen(false)}
                    disabled={loading}
                  >
                    Annulla
                  </Button>
                  <Button type="submit" loading={loading}>
                    Salva Contratto
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
